import {
  ButtonClickedEvent,
  LinkClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import { CTAKind, PurchaseIntent, valueForPurchaseIntent } from '@atlassian/mpac-app-listing';
import { useStatsigWithUpdatedUser } from '@atlassian/mpac-feature-flags';
import { CompatibleProductId } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { useSwallowQueryParam } from '@atlassian/mpac-ui';
import { getDefaultSelectedPlan, MarketplaceAppUtils } from '@atlassian/mpac-utils';
import { capitalize } from 'lodash';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { useEffect, useState } from 'react';

import {
  safeToForwardToPartner,
  trackBuyButton,
  trackDownloadLink,
  trackGetItNow,
  trackPricingCalculatorGetItNow,
  trackTryButton,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_PLANS_PRICING,
  ANALYTICS_EVENT_CONTAINER_PRICING_TAB,
  ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
  ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
  ANALYTICS_EVENT_SOURCE_HOSTING_MODEL,
  ANALYTICS_EVENT_SOURCE_PLANS_PRICING,
  ANALYTICS_EVENT_SOURCE_PRICING_TAB,
  ANALYTICS_SUBJECT_ID_CTA,
  ANALYTICS_SUBJECT_ID_REQUEST_APP_ADMIN,
} from '../../../../../../../analytics/Events';
import { getEmceeAppListingInstallLink, onlyCompatibleProducts } from '../AppsRoute.utils';
import { toAnalyticsHosting, toAnalyticsHostingForHostingSelector } from '../utils';
import { useDisambiguatedSiteAndProduct } from './useDisambiguatedSiteAndProduct';

import type {
  CompatibleProduct,
  HostingSelectorAppVersion,
  PurchaseIntentType,
} from '@atlassian/mpac-app-listing';
import type {
  ArtifactDownloadLink,
  HostingWithCloudComplianceBoundariesType,
  SelectedEditionType,
} from '@atlassian/mpac-types';
import type {
  MarketplaceAppPaymentModel,
  MarketplaceBillingCycle as BillingCycle,
  MarketplaceStoreEditionType,
} from '@atlassian/mpac-types/graphql-gateway-types';
import type { MouseEvent } from 'react';

const trackCTAClickEvent = (purchaseIntent: PurchaseIntentType) =>
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: trackTryButton,
    [PurchaseIntent.BUY]: trackBuyButton,
    [PurchaseIntent.GET]: function doNothing() {
      /* We haven't been sending events for this historically */
    },
  });

const trackHostingModalCTAClickEvent = (purchaseIntent: string | 'buy' | 'get' | 'try') =>
  // @ts-expect-error [MC-2850] - TS2345 - Argument of type 'string' is not assignable to parameter of type 'PurchaseIntentType'.
  valueForPurchaseIntent(purchaseIntent)({
    [PurchaseIntent.TRY]: trackTryButton,
    [PurchaseIntent.BUY]: trackBuyButton,
    [PurchaseIntent.GET]: trackGetItNow,
  });

const hasSiteSelectorCompatibleProduct = (appVersion?: HostingSelectorAppVersion | null) => {
  const isRemoteable = appVersion && MarketplaceAppUtils.isRemoteable(appVersion.deployment);
  const isBitbucketCompatible = appVersion?.deployment.compatibleProducts.some(
    (product) => product.id === CompatibleProductId.Bitbucket
  );

  return isRemoteable && !isBitbucketCompatible;
};

export const SITE_SELECTOR_VISIBLE_QUERY_PARAM = 'site-selector-visible';

export type OnPricingCalculatorCTAClickArgs = {
  billingCycle: BillingCycle;
  userCount: number;
  label: string;
  selectedPlan?: MarketplaceStoreEditionType;
};

export type UseCTAClickHandlerOptions = {
  appKey: string;
  appId: string;
  userLoggedIn: boolean;
  userEmail: string | null | undefined;
  selectedHosting: HostingWithCloudComplianceBoundariesType;
  paymentModel: MarketplaceAppPaymentModel;
  compatibleProducts: ReadonlyArray<CompatibleProduct>;
  cloudAppVersion: HostingSelectorAppVersion | null | undefined;
  artifactDownloadLink: ArtifactDownloadLink;
  editionTypeList: MarketplaceStoreEditionType[];
  pageRouteSource?: string | null;
};

/**
 * This hook handles all side effects that get triggered after a conversion CTA is clicked on the app listing page.
 * It houses states for the site selector and hosting selector, and handlers for their click events
 */
// eslint-disable-next-line max-lines-per-function -- One hook to handle multiple click events
export const useCTAClickHandler = ({
  appKey,
  appId,
  userLoggedIn,
  userEmail,
  selectedHosting,
  paymentModel,
  compatibleProducts,
  cloudAppVersion,
  artifactDownloadLink,
  editionTypeList,
  pageRouteSource,
}: UseCTAClickHandlerOptions) => {
  const { enableOneClickAppInstall, enableTwoClickAppInstall } = useStatsigWithUpdatedUser();
  const fireAnalyticsEvent = useAnalyticsEvents();
  const siteSelectorQueryValue = useSwallowQueryParam(SITE_SELECTOR_VISIBLE_QUERY_PARAM);

  const [siteSelectorOpen, setSiteSelectorOpen] = useState(!!siteSelectorQueryValue);
  const [hostingSelectorPurchaseIntent] = useState(PurchaseIntent.TRY);
  const [showOneClickAppInstallDialog, setShowOneClickAppInstallDialog] = useState<boolean>(false);
  const [showTwoClickAppInstallFocusedTask, setShowTwoClickAppInstallFocusedTask] =
    useState<boolean>(false);

  const [activeCTAKind] = useState(CTAKind.PRIMARY);
  const [selectedPlanFromPricingTab, setSelectedPlanFromPricingTab] = useState<
    MarketplaceStoreEditionType | undefined
  >(undefined);
  const defaultSelectedPlanValue =
    selectedPlanFromPricingTab ?? getDefaultSelectedPlan(editionTypeList);
  const defaultSelectedPlan = {
    label: capitalize(defaultSelectedPlanValue),
    value: defaultSelectedPlanValue,
  };

  const [selectedPlanForPlanSelector, setSelectedPlanForPlanSelector] =
    useState<SelectedEditionType>(defaultSelectedPlan);

  useEffect(() => {
    if (selectedPlanFromPricingTab) {
      setSelectedPlanForPlanSelector({
        label: capitalize(selectedPlanFromPricingTab),
        value: selectedPlanFromPricingTab,
      });
    }
  }, [selectedPlanFromPricingTab]);

  const cloudAppCompatibilities = cloudAppVersion
    ? onlyCompatibleProducts(compatibleProducts).map((product) => product.id)
    : [];

  const {
    furtherDisambiguationNeeded,
    disambiguationResult,
    availableCloudSitesCount,
    availableSiteCloudIds,
  } = useDisambiguatedSiteAndProduct(userLoggedIn, cloudAppCompatibilities, paymentModel);

  const installAppHrefResolver = (selectedSiteProduct) =>
    getEmceeAppListingInstallLink(appKey, selectedSiteProduct, userEmail);

  const trackPossibleArtifactDownloadClick = (
    e: MouseEvent<HTMLElement>,
    appVersion?: HostingSelectorAppVersion | null
  ) => {
    const { currentTarget } = e;
    if (!(currentTarget instanceof window.Element) || !appVersion) return;
    const href = currentTarget.getAttribute('href');
    if (!href) return;

    const artifactDownloadUrl = artifactDownloadLink.expand({
      appId,
      buildNumber: appVersion.buildNumber,
    });

    if (href === artifactDownloadUrl) {
      trackDownloadLink(artifactDownloadUrl);
    }
  };

  const handleCloudAppDisambiguation = () => {
    const oneClickInstallEnabled = userLoggedIn && enableOneClickAppInstall();
    const twoClickInstallEnabled =
      userLoggedIn && enableTwoClickAppInstall() && !oneClickInstallEnabled;
    if (oneClickInstallEnabled !== showOneClickAppInstallDialog) {
      setShowOneClickAppInstallDialog(oneClickInstallEnabled);
    }
    if (!oneClickInstallEnabled && twoClickInstallEnabled !== showTwoClickAppInstallFocusedTask) {
      setShowTwoClickAppInstallFocusedTask(twoClickInstallEnabled);
    }
    if (oneClickInstallEnabled || twoClickInstallEnabled || furtherDisambiguationNeeded) {
      setSiteSelectorOpen(true);
    } else if (disambiguationResult) {
      window.location.href = installAppHrefResolver(disambiguationResult);
    }
  };

  const onAppListingHeaderCTAClick = (
    e: MouseEvent<HTMLElement>,
    productHostingOptions: ReadonlyArray<HostingWithCloudComplianceBoundariesType>,
    purchaseIntent: PurchaseIntentType,
    selectedVersion?: HostingSelectorAppVersion | null
  ) => {
    setSelectedPlanFromPricingTab(undefined);
    const analyticsHosting = toAnalyticsHosting(selectedHosting);
    const cloudSupported = productHostingOptions.includes(AtlassianProductHostingType.Cloud);

    const { target } = e || {};
    if (target instanceof window.Element) {
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        eventCategory: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
        source: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        attributes: {
          // eslint-disable-next-line unicorn/prefer-dom-node-text-content
          // @ts-expect-error [MC-2850] - TS2339 - Property 'innerText' does not exist on type 'Element'.
          label: target.innerText,
          eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
          source: ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
          hosting: camelCase(selectedHosting),
          availableCloudSitesCount,
          ...(pageRouteSource && { pageRouteSource }),
        },
      });
    }

    safeToForwardToPartner(appId, () => {
      trackCTAClickEvent(purchaseIntent)(analyticsHosting);
      trackPossibleArtifactDownloadClick(e, selectedVersion);
    });

    if (cloudSupported && hasSiteSelectorCompatibleProduct(selectedVersion)) {
      handleCloudAppDisambiguation();
    }
  };

  const onHostingSelectorCTAClick = (
    e: MouseEvent<HTMLElement>,
    hosting: AtlassianProductHostingType,
    appVersion: HostingSelectorAppVersion
  ) => {
    const { target } = e || {};
    if (target instanceof window.Element) {
      fireAnalyticsEvent({
        eventType: ButtonClickedEvent,
        eventCategory: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
        actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
        attributes: {
          // eslint-disable-next-line unicorn/prefer-dom-node-text-content
          // @ts-expect-error [MC-2850] - TS2339 - Property 'innerText' does not exist on type 'Element'.
          label: startCase(toLower(target.innerText)),
          eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
          source: ANALYTICS_EVENT_SOURCE_HOSTING_MODEL,
          hosting: toAnalyticsHostingForHostingSelector(hosting),
          availableCloudSitesCount,
        },
        source: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      });
    }

    safeToForwardToPartner(appId, () => {
      trackHostingModalCTAClickEvent(hostingSelectorPurchaseIntent)(toAnalyticsHosting(hosting));
      trackPossibleArtifactDownloadClick(e, appVersion);
    });

    const cloudSupported = hosting === AtlassianProductHostingType.Cloud;

    if (cloudSupported && hasSiteSelectorCompatibleProduct(appVersion)) {
      handleCloudAppDisambiguation();
    }
  };

  const onPricingCalculatorCTAClick = ({
    billingCycle,
    userCount,
    label,
    selectedPlan,
  }: OnPricingCalculatorCTAClickArgs) => {
    if (selectedPlan) {
      setSelectedPlanFromPricingTab(selectedPlan);
    }

    fireAnalyticsEvent({
      eventType: ButtonClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CTA,
      attributes: {
        label,
        billingCycle,
        userCount,
        source: selectedPlan
          ? ANALYTICS_EVENT_SOURCE_PLANS_PRICING
          : ANALYTICS_EVENT_SOURCE_PRICING_TAB,
        eventContainer: selectedPlan
          ? ANALYTICS_EVENT_CONTAINER_PLANS_PRICING
          : ANALYTICS_EVENT_CONTAINER_PRICING_TAB,
        availableCloudSitesCount,
        selectedPlan,
        ...(pageRouteSource && { pageRouteSource }),
      },
    });

    safeToForwardToPartner(appId, () => {
      trackPricingCalculatorGetItNow(selectedPlan);
    });

    if (hasSiteSelectorCompatibleProduct(cloudAppVersion)) {
      handleCloudAppDisambiguation();
    }
  };

  const onRequestAppClick = () => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_REQUEST_APP_ADMIN,
      source: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_SITE_SELECTOR,
        label: 'Request this app',
        hostingSelected: 'cloud',
        category: ANALYTICS_CATEGORY_APP_LISTING,
      },
    });

    setSiteSelectorOpen(true);
  };
  const focusOnCta = () => {
    document.getElementById('app-listing-cta-button')?.focus();
  };
  const onSiteSelectorClose = () => {
    setSiteSelectorOpen(false);

    // Reset the selected plan to the default value for two step app install
    if (enableTwoClickAppInstall()) {
      setSelectedPlanForPlanSelector(defaultSelectedPlan);
      /* without the timeout, focusing on the CTA is not working */
      setTimeout(() => {
        focusOnCta();
      }, 100);
    }
  };

  return {
    siteSelectorOpen,
    selectedPlan: selectedPlanFromPricingTab,
    onSiteSelectorClose,
    activeCTAKind,
    hostingSelectorPurchaseIntent,
    onRequestAppClick,
    onAppListingHeaderCTAClick,
    onHostingSelectorCTAClick,
    onPricingCalculatorCTAClick,
    showOneClickAppInstallDialog,
    showTwoClickAppInstallFocusedTask,
    availableSiteCloudIds,
    selectedPlanForPlanSelector,
    setSelectedPlanForPlanSelector,
  };
};
